import { Col, Row } from "antd";
import { get } from "lodash";
import { useTranslation } from "react-i18next";
import {
  MdAdminPanelSettings,
  MdOutlineFreeCancellation,
  MdWorkOutline,
} from "react-icons/md";
import Form from "shared/components/Form";
import FormField from "shared/components/FormField";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import { NAME_SPACES } from "shared/locales/constants";
import {
  PROFESSIONAL_SITUATIONS,
  PROFILE_LIST,
  SITUATION_PROFESSIONAL,
  SUBSCRIPTION_FORM_KEYS,
} from "utils/constants";

const MUTUAL_MOBILITY_SITUATION_PROFESSIONAL = {
  [SITUATION_PROFESSIONAL.SALARIED]: SITUATION_PROFESSIONAL.SALARIED,
  [SITUATION_PROFESSIONAL.STUDENT]: SITUATION_PROFESSIONAL.STUDENT,
  [SITUATION_PROFESSIONAL.RETIRED]: SITUATION_PROFESSIONAL.RETIRED,
  [SITUATION_PROFESSIONAL.SEARCHING_FOR_A_JOB]:
    SITUATION_PROFESSIONAL.SEARCHING_FOR_A_JOB,
};

const Activity = ({
  setPercent,
  isFieldRequired,
  initialValues,
  bindField,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.CONTRACT);
  const SUBSCRIPTION = t("SUBSCRIPTION", { returnObjects: true });
  const ACTIVITY = t("SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.ACTIVITY", {
    returnObjects: true,
  });
  const isTns = get(initialValues, "insurancePlan.fields.tns");
  const isRenaultEmployee =
    get(initialValues, "project.contact.fields.profile") ===
    PROFILE_LIST.RENAULT_EMPLOYEE;

  const matriculationPath = "project.contact.fields.matriculation";
  const matriculationField = bindField(matriculationPath, {
    label: ACTIVITY.MATRICULATION.LABEL,
    required: isFieldRequired(matriculationPath),
    onBlur: () => setPercent(initialValues),
  });

  const socioProfessionalCategoryPath =
    "project.contact.socioProfessionalCategory";
  const socioProfessionalCategoryField = bindField(
    socioProfessionalCategoryPath,
    {
      label: isRenaultEmployee
        ? ACTIVITY.SITUATION.LABEL
        : ACTIVITY.SITUATION_PROFESSIONAL.LABEL,
      required: isFieldRequired(socioProfessionalCategoryPath),
      onChange: () => {
        if (!isRenaultEmployee) {
          setPercent(initialValues);
        }
      },
      onBlur: () => setPercent(initialValues),
    },
  );

  const socialReasonPath = "project.contact.fields.socialReason";
  const socialReasonField = bindField(socialReasonPath, {
    label: ACTIVITY.SOCIAL_REASON.LABEL,
    required: isFieldRequired(socialReasonPath),
    onBlur: () => setPercent(initialValues),
  });

  const siretNumberPath = "project.contact.siretNumber";
  const siretNumberField = bindField(siretNumberPath, {
    label: ACTIVITY.SIRET.LABEL,
    required: isFieldRequired(siretNumberPath),
    onBlur: () => setPercent(initialValues),
  });

  const nafCodePath = "project.contact.fields.nafCode";
  const nafCodeField = bindField(nafCodePath, {
    label: ACTIVITY.NAF_CODE.LABEL,
    required: isFieldRequired(nafCodePath),
    onBlur: () => setPercent(initialValues),
  });

  const organizationNumberPath = "project.contact.fields.organizationNumber";
  const organizationNumberField = bindField(organizationNumberPath, {
    label: ACTIVITY.ORGANIZATION_NUMBER.LABEL,
    required: isFieldRequired(organizationNumberPath),
    onBlur: () => setPercent(initialValues),
  });

  return (
    <div
      className="subscription-form--wrapper"
      id={`${SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.ACTIVITY}-section`}
    >
      <Form type="vertical" title={ACTIVITY.TITLE}>
        <Row gutter={[16, 16]}>
          <Col lg={24} xs={24}>
            <Input
              label={SUBSCRIPTION.FORM.PROFILE.LABEL}
              icon={<MdAdminPanelSettings size={16} />}
              defaultValue={
                SUBSCRIPTION.FORM.PROFILE.OPTIONS[
                  get(initialValues, "project.contact.fields.profile")
                ]
              }
              required={isFieldRequired("project.contact.fields.profile")}
              disabled={true}
            />
          </Col>
          {isRenaultEmployee ? (
            <>
              <Col lg={24} xs={24}>
                <FormField
                  path={matriculationField.path}
                  label={matriculationField.label}
                  required={matriculationField.required}
                  disabled={matriculationField.disabled}
                >
                  {(field) => (
                    <Input
                      placeholder={ACTIVITY.MATRICULATION.PLACEHOLDER}
                      label={matriculationField.label}
                      icon={<MdOutlineFreeCancellation size={16} />}
                      defaultValue={matriculationField.defaultValue}
                      {...field}
                      onChange={matriculationField.onChange}
                      onBlur={matriculationField.onBlur}
                    />
                  )}
                </FormField>
              </Col>
              <Col lg={24} xs={24}>
                <FormField
                  path={socioProfessionalCategoryField.path}
                  label={socioProfessionalCategoryField.label}
                  required={socioProfessionalCategoryField.required}
                  disabled={socioProfessionalCategoryField.disabled}
                >
                  {(field) => (
                    <Select
                      options={Object.entries(PROFESSIONAL_SITUATIONS).map(
                        ([key, value]) => ({
                          value,
                          label: ACTIVITY.SITUATION.OPTIONS[key],
                        }),
                      )}
                      label={socioProfessionalCategoryField.label}
                      icon={<MdWorkOutline size={16} />}
                      placeholder={ACTIVITY.SITUATION.PLACEHOLDER}
                      defaultValue={socioProfessionalCategoryField.defaultValue}
                      {...field}
                      onChange={socioProfessionalCategoryField.onChange}
                      onBlur={socioProfessionalCategoryField.onBlur}
                    />
                  )}
                </FormField>
              </Col>
            </>
          ) : (
            <Col lg={24} xs={24}>
              <FormField
                path={socioProfessionalCategoryField.path}
                label={socioProfessionalCategoryField.label}
                required={socioProfessionalCategoryField.required}
                disabled={socioProfessionalCategoryField.disabled}
              >
                {(field) => (
                  <Select
                    options={Object.entries(
                      MUTUAL_MOBILITY_SITUATION_PROFESSIONAL,
                    ).map(([key, value]) => ({
                      value,
                      label: ACTIVITY.SITUATION.MUTUAL_MOBILITY_OPTIONS[key],
                    }))}
                    label={socioProfessionalCategoryField.label}
                    icon={<MdWorkOutline size={16} />}
                    placeholder={ACTIVITY.SITUATION_PROFESSIONAL.PLACEHOLDER}
                    defaultValue={socioProfessionalCategoryField.defaultValue}
                    {...field}
                    onChange={socioProfessionalCategoryField.onChange}
                    onBlur={socioProfessionalCategoryField.onBlur}
                  />
                )}
              </FormField>
            </Col>
          )}

          {isTns && (
            <>
              <Col lg={24} xs={24}>
                <FormField
                  path={socialReasonField.path}
                  label={socialReasonField.label}
                  required={socialReasonField.required}
                  disabled={socialReasonField.disabled}
                >
                  {(field) => (
                    <Input
                      placeholder={ACTIVITY.SOCIAL_REASON.PLACEHOLDER}
                      label={socialReasonField.label}
                      icon={<MdOutlineFreeCancellation size={16} />}
                      type="custom"
                      normalizeText={true}
                      defaultValue={socialReasonField.defaultValue}
                      {...field}
                      onChange={socialReasonField.onChange}
                      onBlur={socialReasonField.onBlur}
                    />
                  )}
                </FormField>
              </Col>
              <Col lg={24} xs={24}>
                <FormField
                  path={siretNumberField.path}
                  label={siretNumberField.label}
                  required={siretNumberField.required}
                  disabled={siretNumberField.disabled}
                >
                  {(field) => (
                    <Input
                      placeholder={ACTIVITY.SIRET.PLACEHOLDER}
                      label={siretNumberField.label}
                      icon={<MdOutlineFreeCancellation size={16} />}
                      type="custom"
                      stripSpaces={true}
                      defaultValue={siretNumberField.defaultValue}
                      {...field}
                      onChange={siretNumberField.onChange}
                      onBlur={siretNumberField.onBlur}
                    />
                  )}
                </FormField>
              </Col>
              <Col lg={24} xs={24}>
                <FormField
                  path={nafCodeField.path}
                  label={nafCodeField.label}
                  required={nafCodeField.required}
                  disabled={nafCodeField.disabled}
                >
                  {(field) => (
                    <Input
                      placeholder={ACTIVITY.NAF_CODE.PLACEHOLDER}
                      label={nafCodeField.label}
                      icon={<MdOutlineFreeCancellation size={16} />}
                      type="custom"
                      stripSpaces={true}
                      defaultValue={nafCodeField.defaultValue}
                      {...field}
                      onChange={nafCodeField.onChange}
                      onBlur={nafCodeField.onBlur}
                    />
                  )}
                </FormField>
              </Col>
              <Col lg={24} xs={24}>
                <FormField
                  path={organizationNumberField.path}
                  label={organizationNumberField.label}
                  required={organizationNumberField.required}
                  disabled={organizationNumberField.disabled}
                >
                  {(field) => (
                    <Input
                      placeholder={ACTIVITY.ORGANIZATION_NUMBER.PLACEHOLDER}
                      label={organizationNumberField.label}
                      icon={<MdOutlineFreeCancellation size={16} />}
                      type="custom"
                      stripSpaces={true}
                      defaultValue={organizationNumberField.defaultValue}
                      {...field}
                      onChange={organizationNumberField.onChange}
                      onBlur={organizationNumberField.onBlur}
                    />
                  )}
                </FormField>
              </Col>
            </>
          )}
        </Row>
      </Form>
    </div>
  );
};

export default Activity;
